<script setup lang="ts">
/**
 * 代理商的客戶（`/reseller/customers`）——批93-C §2.2。
 *
 * 兩件事：建客戶、幫客戶開 portal 帳號。
 *
 * ★ **沒有「認領既有客戶」**：客戶歸屬是單一 nullable FK（一個客戶只能掛一個代理商），
 *   建立時自動寫成自己，之後的改派只有我方做得到。這不是權限保守，是資料模型上就只有
 *   一條路——同一個客戶同時屬於兩家代理商在計費與可見性上都無解。
 * ★ 網域撞到別家客戶時後端回 409，訊息**不含對方名稱**——不然這支端點就成了客戶名單探測器。
 * ★ **keyset 分頁**（批93-D）：後端回 `{items, next_cursor}`，一頁 50 家。建立／修改後
 *   `load()` 一律**回到第一頁**——新客戶就在最上面，那正是使用者要看的。
 */
import { computed, onMounted, ref } from 'vue'

import CustomerProfileCard from '@/components/CustomerProfileCard.vue'
import UiButton from '@/components/UiButton.vue'
import { ApiError } from '@/api/client'
import * as reseller from '@/api/reseller'
import type {
  ResellerCustomer,
  ResellerCustomerDetail,
  ResellerCustomerUser,
} from '@/api/reseller'
import type { CustomerPatchBody } from '@/api/ls'
import { vEnter } from '@/lib/enterKey'
import { useAuthStore } from '@/stores/auth'
import { useDialogsStore } from '@/stores/dialogs'

const auth = useAuthStore()
const dialogs = useDialogsStore()

const rows = ref<ResellerCustomer[]>([])
const loading = ref(true)
const loadError = ref('')
/** keyset 續頁游標（null＝已經載完）。 */
const nextCursor = ref<string | null>(null)
const loadingMore = ref(false)

/** 重載＝**回到第一頁**（建立／改名之後要看到的就是最上面那筆）。 */
async function load() {
  const page = await reseller.listCustomers()
  rows.value = page.items
  nextCursor.value = page.next_cursor
}

async function loadMore() {
  if (!nextCursor.value || loadingMore.value) return
  loadingMore.value = true
  try {
    const page = await reseller.listCustomers({ cursor: nextCursor.value })
    rows.value = [...rows.value, ...page.items]
    nextCursor.value = page.next_cursor
  } catch (e) {
    await fail(e, '讀取下一頁失敗')
  } finally {
    loadingMore.value = false
  }
}
onMounted(async () => {
  try {
    await load()
    loadError.value = ''
  } catch (e) {
    loadError.value = errText(e, '讀取客戶清單失敗')
  } finally {
    loading.value = false
  }
})

function errText(e: unknown, fallback: string): string {
  const d = e instanceof ApiError && e.payload ? (e.payload as { detail?: unknown }).detail : undefined
  if (d == null) return fallback
  if (typeof d === 'string') return d
  const obj = d as { message?: string; code?: string }
  if (obj.message) return obj.message
  const CODES: Record<string, string> = {
    domain_taken: '此網域已被其他客戶使用，請聯絡我方確認。',
    email_in_other_org: '這個 Google 帳號已經屬於另一個組織，請聯絡我方處理。',
  }
  if (obj.code) return CODES[obj.code] ?? obj.code
  return JSON.stringify(d)
}
async function fail(e: unknown, fallback: string) {
  await dialogs.notify({ title: '操作失敗', message: errText(e, fallback) })
}

// --- 新增客戶 ---------------------------------------------------------------
const newName = ref('')
const newDomain = ref('')
const creating = ref(false)
async function createCustomer() {
  const name = newName.value.trim()
  if (!name) return
  creating.value = true
  try {
    await reseller.createCustomer({ name, domain: newDomain.value.trim() || null })
    newName.value = ''
    newDomain.value = ''
    await load()
  } catch (e) {
    await fail(e, '建立客戶失敗')
  } finally {
    creating.value = false
  }
}

// --- 選取／編輯 -------------------------------------------------------------
const selected = ref<ResellerCustomer | null>(null)
/**
 * 選中客戶的詳情（清單投影 ＋ `profile`）。批97 Bug 7 起要多打一次
 * `GET /customers/{id}`——清單那份**沒有** `profile`（`load_only` 刻意不載
 * `profile_json`，效能硬規則⑥），基本資料卡吃的就是這一份。
 */
const detail = ref<ResellerCustomerDetail | null>(null)
const users = ref<ResellerCustomerUser[]>([])
const editing = ref(false)
const editName = ref('')
const editDomain = ref('')
const savingCustomer = ref(false)

async function select(c: ResellerCustomer) {
  selected.value = c
  editing.value = false
  users.value = []
  detail.value = null
  // 詳情失敗不必吵（基本資料卡就顯示「載入中…」）；帳號讀不到才要出聲。
  void reseller
    .getCustomer(c.id)
    .then((d) => {
      if (selected.value?.id === c.id) detail.value = d
    })
    .catch(() => {})
  try {
    users.value = await reseller.listCustomerUsers(c.id)
  } catch (e) {
    await fail(e, '讀取 portal 帳號失敗')
  }
}
function startEdit() {
  if (!selected.value) return
  editName.value = selected.value.name
  editDomain.value = selected.value.domain ?? ''
  editing.value = true
}
async function saveCustomer() {
  if (!selected.value) return
  const name = editName.value.trim()
  if (!name) return
  savingCustomer.value = true
  try {
    const updated = await reseller.updateCustomer(selected.value.id, {
      name,
      domain: editDomain.value.trim() || null,
    })
    editing.value = false
    detail.value = updated
    await load()
    selected.value = rows.value.find((c) => c.id === updated.id) ?? updated
  } catch (e) {
    await fail(e, '更新客戶失敗')
  } finally {
    savingCustomer.value = false
  }
}

// --- 基本資料代填（批97 Bug 7）---------------------------------------------
//
// 表單、驗證與唯讀顯示全在共用元件 `components/CustomerProfileCard.vue`——與我方後台
// 的客戶頁**同一支**（後端也共用 `services/customer_patch.py`，422 形狀逐字相同）。
// 代理商對自家客戶一律可編（`can-edit` 恆真）：這個 realm 撈不到別家客戶，
// 而「幫客戶把資料填齊」正是代理商窗口的日常工作。

async function saveProfile(body: CustomerPatchBody) {
  if (!selected.value) throw new Error('no customer selected')
  return await reseller.updateCustomer(selected.value.id, body)
}
/**
 * 存檔成功。回應是 additive（客戶投影 ＋ `profile`），所以不必再打一次 GET；
 * `load()` 重載清單是為了讓左清單的名稱／網域與伺服器一致（代填也可能改到 `name`
 * ——公司帳號的 `company_name` 就是 `customers.name`）。
 */
async function onProfileSaved(updated: unknown) {
  const u = updated as ResellerCustomerDetail
  detail.value = u
  await load()
  selected.value = rows.value.find((c) => c.id === u.id) ?? u
}

// --- portal 帳號 ------------------------------------------------------------
const newUserEmail = ref('')
const addingUser = ref(false)
async function addUser() {
  const email = newUserEmail.value.trim()
  if (!selected.value || !email) return
  addingUser.value = true
  try {
    await reseller.addCustomerUser(selected.value.id, email)
    newUserEmail.value = ''
    users.value = await reseller.listCustomerUsers(selected.value.id)
    await dialogs.notify({
      title: '已新增帳號',
      message: `已為 ${email} 預建 portal 帳號。系統「不會寄信」——請自行通知對方用這個 Google 帳號登入客戶入口。`,
    })
  } catch (e) {
    await fail(e, '新增帳號失敗')
  } finally {
    addingUser.value = false
  }
}
async function removeUser(u: ResellerCustomerUser) {
  if (!selected.value) return
  const ok = await dialogs.confirm({
    message: `移除 portal 帳號 ${u.email}？對方會立刻無法登入客戶入口。`,
    danger: true,
    confirmText: '移除',
  })
  if (!ok) return
  try {
    await reseller.removeCustomerUser(selected.value.id, u.id)
    users.value = await reseller.listCustomerUsers(selected.value.id)
  } catch (e) {
    await fail(e, '移除失敗')
  }
}

function userTag(u: ResellerCustomerUser): string {
  if (u.status === 'invited') return '待首登'
  if (!u.active) return '已停用'
  return u.role === 'owner' ? '管理者' : '成員'
}
const canCreate = computed(() => !!newName.value.trim() && !creating.value)
function fmtDate(iso: string | null | undefined): string {
  return iso ? iso.slice(0, 10) : '—'
}
</script>

<template>
  <div class="page">
    <div class="page-head">
      <h1>我的客戶</h1>
      <span class="count">{{ rows.length }}</span>
      <span v-if="auth.resellerName" class="org">{{ auth.resellerName }}</span>
    </div>

    <p v-if="loadError" class="err">{{ loadError }}</p>
    <div v-if="loading" class="loading-wrap"><span class="spinner lg" /></div>

    <div v-else class="grid">
      <!-- 左：清單＋新增 -->
      <div class="card">
        <div class="create-row">
          <input v-model="newName" placeholder="客戶名稱" v-enter="createCustomer" />
          <UiButton variant="primary" :disabled="!canCreate" :loading="creating" @click="createCustomer">
            新增
          </UiButton>
        </div>
        <input v-model="newDomain" class="domain" placeholder="email 網域（選填，如 acme.com）" />
        <p class="domain-hint">
          網域是客戶識別用（發票／對帳），一個網域只能屬於一家客戶。填錯或撞號可以之後再改。
        </p>
        <ul class="list">
          <li
            v-for="c in rows"
            :key="c.id"
            :class="{ sel: selected?.id === c.id }"
            @click="select(c)"
          >
            {{ c.name }} <small v-if="c.domain" class="dom">@{{ c.domain }}</small>
          </li>
        </ul>
        <p v-if="!rows.length" class="subtle">
          還沒有客戶。先在左上角建立一位，之後才能為他提出授權申請。
        </p>
        <!-- keyset 分頁：後端一頁 50 家。`next_cursor` 非 null 才畫這一列。 -->
        <div v-if="nextCursor" class="more">
          <UiButton size="sm" :loading="loadingMore" @click="loadMore">載入更多</UiButton>
          <span class="subtle">已載入 {{ rows.length }} 家</span>
        </div>
      </div>

      <!-- 右：明細 -->
      <div v-if="selected" class="card">
        <div class="cust-head">
          <template v-if="editing">
            <input v-model="editName" class="cust-in" placeholder="客戶名稱" v-enter="saveCustomer" @keyup.esc="editing = false" />
            <input v-model="editDomain" class="cust-in" placeholder="email 網域（選填）" v-enter="saveCustomer" @keyup.esc="editing = false" />
            <UiButton variant="primary" size="sm" :loading="savingCustomer" @click="saveCustomer">存</UiButton>
            <UiButton size="sm" @click="editing = false">取消</UiButton>
          </template>
          <template v-else>
            <h2 class="cust-name">{{ selected.name }}</h2>
            <small v-if="selected.domain" class="dom">@{{ selected.domain }}</small>
            <button class="x" title="修改名稱／網域" @click="startEdit">✎</button>
          </template>
        </div>
        <p class="subtle">建立於 {{ fmtDate(selected.created_at) }}</p>

        <!-- 基本資料（批97 Bug 7）：與我方後台同一支共用元件、同一套驗證 -->
        <p v-if="!detail" class="subtle">載入中…</p>
        <CustomerProfileCard
          v-else
          :profile="detail.profile"
          :notes="selected.notes"
          :can-edit="true"
          :load-options="reseller.getCustomerProfileOptions"
          :save="saveProfile"
          @saved="onProfileSaved"
        />

        <h3 class="section-title">Portal 登入帳號</h3>
        <p class="subtle">
          預先登記客戶的 Google email，對方用那個帳號登入客戶入口就能看到自己的授權、部署狀態與申請進度。
          <strong>系統不會寄信</strong>，請自行通知。
        </p>
        <div class="field-row">
          <input v-model="newUserEmail" placeholder="customer@email.com" v-enter="addUser" />
          <UiButton :loading="addingUser" @click="addUser">授權</UiButton>
        </div>
        <ul class="chips">
          <li v-for="u in users" :key="u.id" class="chip">
            {{ u.email }}
            <small class="utag">{{ userTag(u) }}</small>
            <button class="x" title="移除" @click="removeUser(u)">×</button>
          </li>
        </ul>
        <p v-if="!users.length" class="subtle">尚未登記任何 portal 帳號。</p>

        <h3 class="section-title">授權</h3>
        <p class="subtle">
          要為這位客戶開新授權、續約或加購，請到〈授權申請〉建立草稿；已核發的證在〈授權〉頁下載。
        </p>
        <div class="links">
          <RouterLink class="btn secondary sm" :to="{ name: 'reseller-requests' }">授權申請</RouterLink>
          <RouterLink class="btn secondary sm" :to="{ name: 'reseller-licenses' }">授權</RouterLink>
        </div>
      </div>
      <div v-else class="card placeholder">← 選一個客戶以管理 portal 帳號</div>
    </div>
  </div>
</template>

<style scoped>
.page-head {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  margin-bottom: var(--sp-4);
}
.page-head h1 {
  margin: 0;
  font-size: var(--fs-20);
  color: var(--fg);
}
.count {
  font-size: var(--fs-12);
  color: var(--fg-3);
  background: var(--bg);
  padding: 1px 8px;
  border-radius: 999px;
}
.org {
  margin-left: auto;
  font-size: var(--fs-12);
  color: var(--fg-3);
}
.err {
  color: var(--danger);
  font-size: var(--fs-13);
}
.grid {
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: var(--sp-4);
  align-items: start;
}
/* 手機／平板（批171）：同〈客戶〉頁——右欄釘成 `minmax(0, 1fr)`，別被卡片內表格撐出畫面。 */
@media (--compact) {
  .grid {
    grid-template-columns: 300px minmax(0, 1fr);
  }
  .grid > .card {
    min-width: 0;
  }
}
@media (max-width: 900px) {
  .grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
.create-row {
  display: flex;
  gap: var(--sp-2);
}
/* 批97：`min-width:0` 是這頁跑版的主因——flex item 預設 min-width:auto＝輸入框的內在寬
   （約 200px），300px 的左卡塞不下「輸入框＋新增鈕」，整列溢到右欄。我方客戶頁同一列
   （`CustomersView .create-row input`）本來就有寫，代理商版抄漏。 */
.create-row input {
  flex: 1;
  min-width: 0;
}
.field-row input {
  flex: 1;
  min-width: 0;
}
.domain {
  margin-top: var(--sp-2);
  width: 100%;
}
.domain-hint {
  margin: 4px 0 var(--sp-2);
  font-size: var(--fs-11);
  color: var(--fg-3);
  line-height: var(--lh-base);
}
.list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 60vh;
  overflow: auto;
}
.list li {
  padding: 7px 10px;
  border-radius: var(--r-sm);
  cursor: pointer;
  font-size: var(--fs-13);
  color: var(--fg-2);
}
.list li:hover {
  background: var(--bg-hover);
}
.list li.sel {
  background: var(--brand-50);
  color: var(--brand-700);
  font-weight: 600;
}
.dom {
  color: var(--fg-4);
  font-size: var(--fs-11);
}
.cust-head {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  flex-wrap: wrap;
  margin-bottom: var(--sp-2);
}
.cust-name {
  margin: 0;
  font-size: var(--fs-16);
  color: var(--fg);
}
.cust-in {
  min-width: 160px;
}
.x {
  border: 0;
  background: transparent;
  color: var(--fg-4);
  cursor: pointer;
  padding: 0 4px;
  font-size: var(--fs-13);
}
.x:hover {
  color: var(--fg);
}
.utag {
  margin-left: 4px;
  color: var(--fg-4);
  font-size: var(--fs-10, 10px);
}
.links {
  display: flex;
  gap: var(--sp-2);
  margin-top: var(--sp-2);
}
.placeholder {
  color: var(--fg-4);
  font-size: var(--fs-13);
}
/* keyset 分頁的「載入更多」列（形狀與 ResellerLicensesView 一致） */
.more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp-2);
  padding: var(--sp-3);
  border-top: 1px solid var(--border);
}
</style>
